import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Checkbox } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const MIN_COUNT = 1;
const MAX_COUNT = 100;

// Crockford Base32 alphabet (https://github.com/ulid/spec).
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

const getCrypto = () => {
  if (typeof window !== "undefined" && window.crypto) return window.crypto;
  return null;
};

const randomBytes = (length) => {
  const cryptoObj = getCrypto();
  const bytes = new Uint8Array(length);
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytes;
};

// Encode a 48-bit Unix-millisecond timestamp as the first 10 Crockford chars.
const encodeTime = (ms) => {
  let value = BigInt(ms);
  const out = new Array(10);
  for (let i = 9; i >= 0; i -= 1) {
    out[i] = CROCKFORD[Number(value & 0x1fn)];
    value >>= 5n;
  }
  return out.join("");
};

// Encode 10 random bytes (80 bits) as the trailing 16 Crockford chars.
const encodeRandom = (bytes) => {
  // Treat the 10 bytes as a single 80-bit big-endian integer and slice into
  // 16 groups of 5 bits, MSB-first, so the high-order bits land first.
  let value = 0n;
  for (let i = 0; i < bytes.length; i += 1) {
    value = (value << 8n) | BigInt(bytes[i]);
  }
  const out = new Array(16);
  for (let i = 15; i >= 0; i -= 1) {
    out[i] = CROCKFORD[Number(value & 0x1fn)];
    value >>= 5n;
  }
  return out.join("");
};

// Increment 80-bit random buffer by 1, big-endian. Returns false on overflow.
const incrementRandom = (bytes) => {
  for (let i = bytes.length - 1; i >= 0; i -= 1) {
    if (bytes[i] === 0xff) {
      bytes[i] = 0;
    } else {
      bytes[i] += 1;
      return true;
    }
  }
  return false;
};

const clampCount = (value) => {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) return MIN_COUNT;
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, n));
};

// Generate N ULIDs. When monotonic is true, IDs sharing the same millisecond
// have their random component incremented by 1 rather than re-randomized, so
// the sequence is guaranteed to sort lexicographically (per ULID spec).
const generateBatch = (count, { monotonic }) => {
  const ms = Date.now();
  const timePart = encodeTime(ms);
  const out = new Array(count);
  if (monotonic) {
    const seed = randomBytes(10);
    out[0] = timePart + encodeRandom(seed);
    for (let i = 1; i < count; i += 1) {
      if (!incrementRandom(seed)) {
        // Extremely unlikely: overflowed 80 bits within one ms. Re-seed.
        const fresh = randomBytes(10);
        for (let j = 0; j < 10; j += 1) seed[j] = fresh[j];
      }
      out[i] = timePart + encodeRandom(seed);
    }
  } else {
    for (let i = 0; i < count; i += 1) {
      out[i] = timePart + encodeRandom(randomBytes(10));
    }
  }
  return out;
};

const applyCase = (ulids, lowercase) =>
  lowercase ? ulids.map((u) => u.toLowerCase()) : ulids;

const UlidGenerator = () => {
  const { t } = useTranslation();
  const [count, setCount] = useState(5);
  const [monotonic, setMonotonic] = useState(false);
  const [lowercase, setLowercase] = useState(false);
  const [ulids, setUlids] = useState(() =>
    generateBatch(5, { monotonic: false })
  );

  const monotonicRef = useRef(monotonic);
  const countRef = useRef(count);
  useEffect(() => {
    monotonicRef.current = monotonic;
  }, [monotonic]);
  useEffect(() => {
    countRef.current = count;
  }, [count]);

  const generate = useCallback(() => {
    const next = generateBatch(clampCount(countRef.current), {
      monotonic: monotonicRef.current,
    });
    setUlids(next);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        generate();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [generate]);

  const displayUlids = useMemo(
    () => applyCase(ulids, lowercase),
    [ulids, lowercase]
  );
  const joined = useMemo(() => displayUlids.join("\n"), [displayUlids]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const download = () => {
    if (!joined) return;
    const blob = new Blob([`${joined}\n`], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ulids.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">{t("ulid_gen_title")}</h1>
            <p className="text-gray-600">{t("ulid_gen_intro")}</p>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("ulid_gen_count_label")}
                </label>
                <input
                  type="number"
                  min={MIN_COUNT}
                  max={MAX_COUNT}
                  value={count}
                  onChange={(e) => setCount(clampCount(e.target.value))}
                  className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <StandardButton onClick={generate} type="primary" size="middle">
                {t("ulid_gen_generate")}
              </StandardButton>
              <StandardButton
                onClick={() => copy(joined, t("ulid_gen_copy_all_label"))}
                type="secondary"
                variant="outlined"
                size="middle"
              >
                {t("ulid_gen_copy_all")}
              </StandardButton>
              <StandardButton
                onClick={download}
                type="secondary"
                variant="outlined"
                size="middle"
                disabled={displayUlids.length === 0}
              >
                {t("ulid_gen_download")}
              </StandardButton>

              <span className="hidden sm:inline text-xs text-gray-500 self-center">
                {t("ulid_gen_shortcut_hint")}
              </span>
            </div>

            <div className="flex flex-wrap gap-4">
              <Checkbox
                checked={monotonic}
                onChange={(e) => setMonotonic(e.target.checked)}
              >
                <span className="text-sm">{t("ulid_gen_monotonic_label")}</span>
                <span className="ml-2 text-xs text-gray-500">
                  — {t("ulid_gen_monotonic_hint")}
                </span>
              </Checkbox>
              <Checkbox
                checked={lowercase}
                onChange={(e) => setLowercase(e.target.checked)}
              >
                <span className="text-sm">{t("ulid_gen_lowercase_label")}</span>
                <span className="ml-2 text-xs text-gray-500">
                  — {t("ulid_gen_lowercase_hint")}
                </span>
              </Checkbox>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <ul className="flex flex-col divide-y divide-indigo-100">
                {displayUlids.map((u, idx) => (
                  <li
                    key={`${u}-${idx}`}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <span className="font-mono text-sm break-all text-gray-800">{u}</span>
                    <StandardButton
                      onClick={() => copy(u, t("ulid_gen_ulid_label"))}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("ulid_gen_note_format")}</p>
              <p>{t("ulid_gen_note_sort")}</p>
              <p>{t("ulid_gen_note_monotonic")}</p>
              <p>{t("ulid_gen_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default UlidGenerator;
